/**
 * Continuous, SERVER-SIDE preview health subscription for the IDE.
 *
 * Subscribes to `GET /projects/:id/preview/health/stream` (an SSE stream) while the
 * workspace is open and the sandbox is running. The server runs the real health probe
 * INSIDE the sandbox (curling the Vite dev server), so this works even when the preview
 * is fully crashed — a dead server answers the browser with no data at all
 * ("127.0.0.1 didn't send any data" / ERR_EMPTY_RESPONSE) and the in-iframe bridge
 * can't report it. That's the whole point: the platform owns the check rather than
 * relying on the preview to phone home.
 *
 * Returns the latest `{ status, message, httpStatus, autoFix, downSeq }`. `downSeq`
 * increments on each fresh `down` episode (transition into down), so a consumer can
 * trigger its autonomous fix exactly once per episode via a stable dependency.
 *
 * Auth rides the httpOnly `token` cookie via `withCredentials` (no `?token=` — that
 * would leak the bearer into URLs/logs/Referer), mirroring {@link LogsPanel}'s stream.
 *
 * @module
 */

import { useEffect, useState } from 'react'

import { API_BASE_URL } from '../config.js'
import {
  type PreviewHealthStreamEvent,
  previewHealthStreamPath,
} from '../utilities/ops-utilities.js'

/** Latest preview-health state surfaced to the IDE. */
export interface PreviewHealthState {
  /** `'down'` = crashed/erroring, `'ok'` = loads, `'unknown'` = paused / not yet probed. */
  status: 'ok' | 'down' | 'unknown'
  /** Secret-redacted Vite error — present only while `down`. */
  message?: string
  /** Last HTTP code the probe saw (`0` = no response / ERR_EMPTY_RESPONSE). */
  httpStatus?: number
  /** The project's `/autofix` setting — the gate for the IDE's autonomous fix. */
  autoFix: boolean
  /** Increments on each fresh `down` episode (use as the autonomous-fix trigger). */
  downSeq: number
}

const INITIAL: PreviewHealthState = { status: 'unknown', autoFix: true, downSeq: 0 }

/**
 * How many times in a row the stream may fail while the project itself still
 * answers before the hook stops reconnecting. Resumes when the project id (or
 * `enabled`) changes.
 */
export const PREVIEW_HEALTH_MAX_FAILURES = 5

/**
 * The preview proxy's "nothing will ever answer on this preview" reason (the
 * project was deleted or its preview token rotated) — the `reason` the
 * `/__mol/preview-status` probe answers with, and that a health frame may carry.
 */
export const PREVIEW_GONE_REASON = 'preview-gone'

/**
 * Whether a parsed answer says the preview is gone for good. Treated exactly
 * like a 4xx from the project probe: stop watching, never reconnect.
 *
 * @param value - A parsed stream frame or status answer.
 * @returns True when it carries `reason: 'preview-gone'`.
 */
export function isPreviewGoneAnswer(value: unknown): boolean {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { reason?: unknown }).reason === PREVIEW_GONE_REASON
  )
}

/**
 * What a failed stream says about the project: `'gone'` = the project probe
 * answered 4xx (deleted, access lost, session ended — reconnecting can never
 * work), `'alive'` = the project answered, `'unknown'` = the probe could not
 * reach the API (a restart or a network blip; keep backing off).
 *
 * @param projectId - The project to probe.
 * @returns The probe verdict.
 */
async function probeProject(projectId: string): Promise<'gone' | 'alive' | 'unknown'> {
  try {
    const res = await fetch(`${API_BASE_URL}/projects/${encodeURIComponent(projectId)}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res.status >= 400 && res.status < 500 && res.status !== 429) return 'gone'
    return res.ok ? 'alive' : 'unknown'
  } catch (_error) {
    return 'unknown'
  }
}

/**
 * @param projectId - the open project, or null when none.
 * @param enabled - subscribe only when true (workspace open AND sandbox running).
 */
export function usePreviewHealth(projectId: string | null, enabled: boolean): PreviewHealthState {
  const [state, setState] = useState<PreviewHealthState>(INITIAL)

  useEffect(() => {
    if (!projectId || !enabled) {
      // Reset to the neutral "unknown" state when we stop watching (sandbox sleeping,
      // workspace closed) so a stale `down` overlay never lingers.
      setState((prev) => (prev.status === 'unknown' && prev.downSeq === 0 ? prev : INITIAL))
      return
    }

    // EventSource isn't guaranteed outside a browser (SSR / a non-DOM test env). Without
    // it there's no live health stream — stay 'unknown' (no overlay, no auto-fix) rather
    // than throw and take the whole workspace render down.
    if (typeof EventSource === 'undefined') return

    let cancelled = false
    let es: EventSource | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null
    let attempt = 0
    // Failures in a row while the project probe said it still exists.
    let failuresWhileAlive = 0

    const stop = (): void => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      reconnectTimer = null
      if (es) es.close()
      // Nothing is watching any more: never leave a stale `down` overlay behind.
      setState(INITIAL)
    }

    const scheduleReconnect = (immediate = false): void => {
      if (cancelled || reconnectTimer) return
      // Exponential backoff capped at 30s; the routine lifetime cap reconnects fast.
      const delay = immediate ? 500 : Math.min(30_000, 1000 * 2 ** attempt)
      attempt += 1
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connect()
      }, delay)
    }

    const connect = (): void => {
      if (cancelled) return
      const next = new EventSource(`${API_BASE_URL}${previewHealthStreamPath(projectId)}`, {
        withCredentials: true,
      })
      es = next

      next.onopen = (): void => {
        attempt = 0
        failuresWhileAlive = 0
      }

      next.onmessage = (event): void => {
        try {
          const parsed: unknown = JSON.parse(event.data as string)
          // Gone for good (deleted / token rotated): the same stop as a 4xx
          // project probe — reconnecting can never work.
          if (isPreviewGoneAnswer(parsed)) {
            next.close()
            stop()
            return
          }
          const data = parsed as PreviewHealthStreamEvent
          if (data.type === 'reconnect') {
            next.close()
            scheduleReconnect(true)
            return
          }
          if (data.type === 'preview_health') {
            setState((prev) => ({
              status: data.status,
              message: data.status === 'down' ? data.message : undefined,
              httpStatus: data.httpStatus,
              autoFix: data.autoFix,
              // Bump only on a transition INTO down, so the consumer fires its fix
              // once per episode (the server already emits only on status change).
              downSeq:
                data.status === 'down' && prev.status !== 'down' ? prev.downSeq + 1 : prev.downSeq,
            }))
          }
        } catch (_error) {
          // Ignore malformed events (keep-alives / partial frames) — the next
          // well-formed event continues normally.
        }
      }

      next.onerror = (): void => {
        // CLOSED = the browser gave up; back off and re-create ourselves. CONNECTING
        // = it's already auto-reconnecting (e.g. the routine cap) — stay silent.
        // Before reconnecting, ask whether the project is still there: a deleted
        // project (or an ended session) fails the stream forever.
        if (next.readyState === EventSource.CLOSED) {
          next.close()
          void probeProject(projectId).then((verdict) => {
            if (cancelled) return
            if (verdict === 'gone') {
              stop()
              return
            }
            if (verdict === 'alive') failuresWhileAlive += 1
            if (failuresWhileAlive >= PREVIEW_HEALTH_MAX_FAILURES) {
              stop()
              return
            }
            scheduleReconnect()
          })
        }
      }
    }

    connect()

    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (es) es.close()
    }
  }, [projectId, enabled])

  return state
}
